import { useState } from "react";

import {
  AnalyzeTinybirdResourceResponseBodyType,
  CountryEnum,
  VariationFe,
} from "@intelligems/ig-types";
import { DataType, GraphConfig } from "@intelligems/ig-types";
import { Skeleton, Stack } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import {
  shouldShowProfitData,
  shouldShowProfitPlaceholder,
} from "@src/features/analytics/utils";
import { sortByOrder } from "@src/utils/sortings";
import { UseQueryResult } from "@tanstack/react-query";

import { TabbedNavigation } from "../../shared/components/TabbedNavigation";

import {
  AudienceSegment,
  AudienceSegmentData,
  VariantData,
} from "./AudienceSegment";

type MetricConfig = {
  id: string;
  label: string;
  dataType: DataType;
};

const METRIC_CONFIGS: MetricConfig[] = [
  {
    id: "gross_profit_per_visitor",
    label: "Profit per visitor",
    dataType: "currency",
  },
  { id: "n_visitors", label: "Visitors", dataType: "number" },
  { id: "n_orders", label: "Orders", dataType: "numberNoDecimal" },
  { id: "net_revenue", label: "Net Revenue", dataType: "currency" },
  { id: "conversion_rate", label: "Conversion Rate", dataType: "percent" },
  {
    id: "net_revenue_per_visitor",
    label: "Revenue per visitor",
    dataType: "currency",
  },
  {
    id: "net_revenue_per_order",
    label: "Average order value",
    dataType: "currency",
  },
];

type MobileAudienceTableContentProps = {
  variations: VariationFe[];
  dashboardData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  segmentDataQuery: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  graphConfigs: GraphConfig[];
  testDuration: number;
  tabValue: number;
  controlVariationId: string;
};

/**
 * Mobile-optimized audience table that displays analytics metrics for test variations.
 * Shows one metric at a time using tabbed navigation, with variation data grouped by
 * audience segments (e.g., Desktop/Mobile, countries, etc.).
 */
export const MobileAudienceTableContent = ({
  variations,
  dashboardData,
  segmentDataQuery,
  graphConfigs,
  tabValue,
  controlVariationId,
}: MobileAudienceTableContentProps) => {
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const selectedData = tabValue === 0 ? dashboardData : segmentDataQuery;
  const isLoading = selectedData.some(
    (data) => data.isLoading || data.isPending
  );

  const shouldShowProfit = shouldShowProfitData(
    selectedData[0]?.data?.metrics || []
  );

  const availableMetrics = METRIC_CONFIGS.filter((metricConfig) => {
    // Filter out profit metrics if not available
    if (
      metricConfig.id.includes("profit") &&
      shouldShowProfitPlaceholder(shouldShowProfit, {
        metrics: [metricConfig.id],
      } as GraphConfig)
    ) {
      return false;
    }

    // Check if metric exists in graphConfigs
    return graphConfigs.some((config) =>
      config.metrics.includes(metricConfig.id)
    );
  });

  const metricLabels = availableMetrics.map((m) => m.label);
  const [activeMetricId, setActiveMetricId] = useState<string>(
    availableMetrics[0]?.id || ""
  );

  const currentMetricConfig =
    METRIC_CONFIGS.find((m) => m.id === activeMetricId) || METRIC_CONFIGS[0];

  const graphConfig = graphConfigs.find((c) =>
    c.metrics.includes(activeMetricId)
  );

  const handleMetricChange = (metricLabel: string) => {
    const config = METRIC_CONFIGS.find((m) => m.label === metricLabel);
    if (config) {
      setActiveMetricId(config.id);
    }
  };

  const groupKey = tabValue === 0 ? "all" : "audience";

  // Group data by audience segments
  let audienceSegments: AudienceSegmentData[] = [];

  if (selectedData && selectedData[0]?.data?.metrics) {
    const normalizedData = selectedData[0].data.metrics.map((group: any) => ({
      ...group,
      order:
        variations.find((variation) => variation.id === group.variation_id)
          ?.order ?? 0,
    }));

    // Group by audience segment
    const groups: Record<string, any[]> = {};
    normalizedData.forEach((item: any) => {
      const groupName = item[groupKey] || "Other";
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(item);
    });

    // Sort variations within each group
    Object.keys(groups).forEach((groupName) => {
      if (Array.isArray(groups[groupName])) {
        groups[groupName] = groups[groupName].sort(sortByOrder);
      }
    });

    // Convert to array of audience segments
    audienceSegments = Object.keys(groups).map((groupName) => {
      const segmentVariations: VariantData[] = groups[groupName].map(
        (data: any) => {
          const variation = variations.find((v) => v.id === data.variation_id);
          const metricValue = data[activeMetricId];

          return {
            variation_id: data.variation_id,
            name: variation?.name || "Unknown",
            value: metricValue?.value,
            uplift: metricValue?.uplift,
            order: data.order,
            isControl: data.variation_id === controlVariationId,
          };
        }
      );

      // Format segment name
      let segmentName = groupName;
      if (tabValue === 5 && groupName !== "Other") {
        // Format country codes
        segmentName = `${CountryEnum[groupName as keyof typeof CountryEnum]} (${groupName})`;
      } else if (groupName === "Youtube") {
        segmentName = "YouTube";
      }

      return {
        segmentName,
        segmentValue: groupName,
        variations: segmentVariations,
      };
    });

    // Sort segments by audience order if available
    const audienceOrder = selectedData[0]?.data?.audienceOrder || [];
    if (audienceOrder.length > 0) {
      audienceSegments.sort((a, b) => {
        const indexA = audienceOrder.indexOf(a.segmentValue);
        const indexB = audienceOrder.indexOf(b.segmentValue);
        return indexA - indexB;
      });
    }
  }

  if (availableMetrics.length === 0) {
    return null;
  }

  return (
    <Stack gap={4}>
      <TabbedNavigation
        tabs={metricLabels}
        onTabChange={handleMetricChange}
        defaultTab={metricLabels[0]}
        buttonSize="extraSmall"
        buttonSx={{ fontSize: 12 }}
      />
      <Stack>
        {audienceSegments.map((segment, i) => (
          <AudienceSegment
            key={i}
            segment={segment}
            dataType={currentMetricConfig.dataType}
            currency={
              storeCurrencyConfig?.value || {
                code: "USD",
                locale: "en-US",
              }
            }
            graphConfig={graphConfig}
            showSegmentTitle={tabValue !== 0}
          />
        ))}
      </Stack>
      {isLoading && !audienceSegments.length && (
        <Stack direction="column" p={2} gap={4}>
          <Skeleton variant="rectangular" width="100%" height={16} />
          <Skeleton variant="rectangular" width="80%" height={16} />
        </Stack>
      )}
    </Stack>
  );
};
